import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { ServiceAdviceBlock } from "@/components/ServiceAdviceBlock";
import { ServiceCautionBlock } from "@/components/ServiceCautionBlock";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { PENSION_FAQ_CATEGORIES } from "@/content/pension-corpus/faq-defs";

const ADVICE_EN = [
  "Read the fund's own instructions first — each fund names a different set of accepted certifiers.",
  "Start 6-8 weeks before the deadline to absorb embassy appointment, legalization and postal delays.",
  "Never pre-sign at home; a life certificate must be signed in front of the consular officer or notarial services attorney.",
  "Check the acceptable date window — many forms require certification within 30 or 60 days of receipt.",
  "Spell names exactly as in the valid passport and keep the spelling identical across the whole document set.",
  "Keep bank credit advices; Immigration accepts them as evidence of monthly income for extensions of stay.",
  "ABTC applicants should obtain a fresh company certificate and police clearance early, since every economy screens applicants.",
  "Replace the ABTC when the passport changes, otherwise the APEC lane cannot be used.",
];

const CAUTIONS_EN = [
  "Late returns cause payment suspension, which can leave a retirement extension short of the required credits.",
  "Self-drafted forms lack the pensioner reference the fund's system needs to match the document to your file.",
  "Blank witness boxes where the form requires non-relative, non-beneficiary witnesses.",
  "Assuming an Immigration residence certificate substitutes for a proof of life — they are different documents.",
  "Submitting translations without Department of Consular Affairs certification where the recipient requires the stamp.",
  "Not reporting a bank change, or using an account whose name does not match the pensioner's.",
  "Heirs spending amounts credited after the date of death — funds reclaim these in nearly all cases.",
  "Treating an ABTC as work authorisation; it covers short business travel only.",
];

export const Route = createFileRoute("/en/pension/faq/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/pension-corpus");
    return { faqs: m.PENSION_FAQ_EN };
  },
  component: PensionFaqHubEn,
  head: ({ loaderData }) => {
    const faqs = loaderData?.faqs ?? [];
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `Proof of Life & Pension Paperwork in Thailand — ${faqs.length} FAQs`,
        description:
          "Proof-of-life certificates for foreign pensioners in Thailand: embassy certification, notarial services attorneys, consular legalization, survivor pensions, and the APEC Business Travel Card.",
        path: "/en/pension/faq",
        type: "article",
        locale: "en_US",
        alternates: [{ lang: "th", path: "/pension/faq" }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "Pension & ABTC FAQ", url: `${site}/en/pension/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/en/pension/faq")) },
      ],
    };
  },
});

function PensionFaqHubEn() {
  const { faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span> <span>Pension &amp; ABTC</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          Proof of life, foreign pension paperwork and the APEC Business Travel Card
        </h1>
        <p className="mb-6 text-muted-foreground" data-speakable="true">
          {faqs.length} answers on keeping a foreign pension paid while living in Thailand — certification routes through your
          embassy, a Notarial Services Attorney and the Department of Consular Affairs — plus ABTC applications through the
          Department of Foreign Trade.
        </p>

        <section aria-label="Categories" className="mb-10">
          <h2 className="mb-3 text-xl font-bold">Browse by category</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {PENSION_FAQ_CATEGORIES.map((c) => (
              <a key={c.slug} href={`/en/pension/faq/${c.slug}`} className="rounded-lg border bg-card p-4 hover:bg-muted">
                <span className="block font-semibold">{c.titleEn}</span>
                <span className="mt-1 block text-xs text-muted-foreground">{c.descEn}</span>
              </a>
            ))}
          </div>
        </section>

        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">{item.q}</summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>

        <ServiceAdviceBlock items={ADVICE_EN} locale="en" />
        <ServiceCautionBlock items={CAUTIONS_EN} locale="en" />

        <section className="mt-10 rounded-lg border bg-card p-5">
          <h2 className="mb-2 text-lg font-bold">Let us assemble your pension file</h2>
          <p className="text-sm text-muted-foreground">
            Tell us the fund, the paying country and your deadline by phone, LINE or email, Mon-Sat 9:00-18:00.
          </p>
          <a href="/contact" className="mt-3 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">Contact us</a>
        </section>
        <div className="mt-8 rounded-lg border bg-muted/30 p-4 text-sm">
          <strong>ภาษาไทย:</strong>{" "}
          <a href="/pension/faq" className="text-primary underline">อ่านหน้านี้เป็นภาษาไทย →</a>
        </div>
        <FaqSourceNote topic="pension" locale="en" />
      </main>
      <SiteFooter />
    </>
  );
}